import React from "react";
import { Flex, theme, Typography } from "antd";
import { useTranslate } from "@refinedev/core";
import styled from "styled-components";
import { SquareIcon } from "../../SquareIcon";
import { extStyle } from "../../../helpers/fileList";
import { DocumentFile } from "../types";

interface FileIdentityProps {
  file: DocumentFile;
  /** Show the "knowledge base only" badge when the file is hidden. */
  showHiddenBadge?: boolean;
}

/**
 * Extension chip + file name + size — the shared way a document is presented,
 * used by both the file row and the preview modal so they can't drift apart.
 */
export const FileIdentity: React.FC<FileIdentityProps> = ({
  file,
  showHiddenBadge = false,
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const { color, bg } = extStyle(file.name);

  return (
    <>
      <SquareIcon
        size={38}
        content={<ExtLabel>{file.ext}</ExtLabel>}
        style={{ background: bg, color, borderRadius: 10 }}
      />
      <Flex vertical flex={1} style={{ minWidth: 0 }}>
        <Typography.Text
          strong
          ellipsis={{ tooltip: file.name }}
          style={{ display: "block" }}
        >
          {file.name}
        </Typography.Text>
        <Typography.Text
          type="secondary"
          style={{ fontSize: token.fontSizeSM }}
        >
          <span>{file.size}</span>
          {showHiddenBadge && file.hidden ? (
            <HiddenTag>
              {t("createExperience.addOns.documents.knowledgeBaseOnly")}
            </HiddenTag>
          ) : null}
        </Typography.Text>
      </Flex>
    </>
  );
};

// ─── Styled components ───────────────────────────────────────────────────────

const ExtLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
`;

const HiddenTag = styled.span`
  &::before {
    content: "·";
    margin: 0 6px;
  }
`;
